import { API, API_BASE } from '../config/api';
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Play, Video, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import CommentSection from '../components/CommentSection';
import LikeButton from '../components/LikeButton';
import FireButton from '../components/FireButton';


function getEmbedUrl(url) {
  if (!url) return '';
  const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]+)/);
  if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}`;
  const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
  if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
  return url;
}

function getYtThumbnail(url) {
  if (!url) return null;
  const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]+)/);
  return match ? `https://img.youtube.com/vi/${match[1]}/hqdefault.jpg` : null;
}

export default function Videos() {
  const [videos, setVideos] = useState([]);
  const [featured, setFeatured] = useState(null);
  const [settings, setSettings] = useState({});

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/videos`).catch(() => ({ data: [] })),
      axios.get(`${API}/site-settings`).catch(() => ({ data: {} }))
    ]).then(([vRes, sRes]) => {
      const vids = vRes.data;
      setVideos(vids);
      setFeatured(vids.find(v => v.is_featured) || vids[0] || null);
      setSettings(sRes.data);
    });
  }, []);

  const sectionTitle = settings?.section_titles?.videos || 'VISUALS';

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-12">
        <div className="flex items-center gap-4 mb-12">
          <Link to="/">
            <Button variant="outline" className="rounded-none border-[#262626] hover:border-[#FF3B30]" data-testid="back-home-btn">
              <ArrowLeft className="w-4 h-4" />
            </Button>
          </Link>
          <div>
            <p className="overline text-[#FF3B30] mb-1">WATCH</p>
            <h1 className="font-cabinet text-3xl sm:text-5xl font-black uppercase tracking-tighter" data-testid="videos-page-title">
              {sectionTitle}
            </h1>
          </div>
        </div>

        {featured && (
          <div className="border border-[#262626] overflow-hidden mb-8" data-testid="featured-video-main">
            <div className="aspect-video bg-black">
              <iframe
                src={getEmbedUrl(featured.url)}
                title={featured.title}
                className="w-full h-full"
                allowFullScreen
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              />
            </div>
            <div className="p-5 bg-[#141414]">
              <h2 className="font-cabinet font-bold text-xl uppercase">{featured.title}</h2>
              {featured.description && <p className="text-[#A3A3A3] text-sm mt-1">{featured.description}</p>}
              <div className="flex items-center gap-5 mt-4">
                <LikeButton itemType="video" itemId={featured.id} size="md" />
                <FireButton itemType="video" itemId={featured.id} size="md" />
              </div>
              <CommentSection itemType="video" itemId={featured.id} />
            </div>
          </div>
        )}

        {videos.length > 1 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {videos.filter(v => v.id !== featured?.id).map(video => (
              <div
                key={video.id}
                className="bg-[#141414] border border-[#262626] overflow-hidden group"
                data-testid={`video-card-${video.id}`}
              >
                <div
                  className="aspect-video bg-black overflow-hidden relative cursor-pointer"
                  onClick={() => { setFeatured(video); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                >
                  {getYtThumbnail(video.url) ? (
                    <img src={getYtThumbnail(video.url)} alt={video.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Video className="w-8 h-8 text-[#262626]" />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <Play className="w-10 h-10 text-white" />
                  </div>
                </div>
                <div className="p-4">
                  <h3 className="font-bold text-sm uppercase truncate">{video.title}</h3>
                  {video.description && <p className="text-[#A3A3A3] text-xs mt-1 line-clamp-2">{video.description}</p>}
                  <div className="flex items-center gap-4 mt-3">
                    <LikeButton itemType="video" itemId={video.id} />
                    <FireButton itemType="video" itemId={video.id} />
                  </div>
                  <CommentSection itemType="video" itemId={video.id} />
                </div>
              </div>
            ))}
          </div>
        )}

        {videos.length === 0 && (
          <div className="text-center py-20">
            <Video className="w-16 h-16 mx-auto text-[#262626] mb-4" />
            <p className="text-[#A3A3A3]">No videos yet</p>
          </div>
        )}
      </div>
    </div>
  );
}
